import React, { useMemo, useState } from 'react';

import {
  formatDispatchDate,
  getDispatchItemBarcode,
  getDispatchItemBrand,
  getDispatchItemCategory,
  getDispatchItemProductName,
  getDispatchItemUnit,
} from '../utils/dispatchDisplay';
import { formatStockQuantity } from '../utils/stockDisplay';
import { getNextSortConfig, sortRows } from '../utils/tableSort';

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const formatPrice = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return Math.ceil(numeric).toFixed(2);
};

const formatPercent = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return `${numeric.toFixed(2)}%`;
};

const effectiveDiscountPercent = (mrp, dprice) => {
  const mrpValue = Number(mrp);
  const saleValue = Number(dprice);
  if (!Number.isFinite(mrpValue) || mrpValue <= 0 || !Number.isFinite(saleValue)) return '';
  return Math.max(0, Math.floor(((mrpValue - saleValue) / mrpValue) * 100));
};

const getSalesMrp = (item) =>
  firstValue(item?.mrp_amount, item?.mrpAmount, item?.sales_mrp, item?.salesMrp, item?.unit_mrp);

const getDiscountPrice = (item) =>
  firstValue(item?.dprice, item?.discounted_price, item?.discountedPrice);

const getDiscountPercent = (item) =>
  firstValue(
    item?.configured_discount_percent,
    item?.discount_percent,
    item?.discountPercent,
    effectiveDiscountPercent(getSalesMrp(item), getDiscountPrice(item))
  );

const statusClass = (status) => {
  const normalized = String(status || 'intransit').toLowerCase();

  if (normalized === 'reached' || normalized === 'received_to_outlet') {
    return 'bg-green-100 text-green-800';
  }

  if (normalized === 'cancelled') return 'bg-red-100 text-red-800';

  return 'bg-indigo-100 text-indigo-800';
};

const getTransitRows = (transitProducts, dispatchOrders) => {
  if (transitProducts?.length) {
    return transitProducts.flatMap((transit) => {
      const order =
        transit.dispatch_order ||
        transit.dispatchOrder ||
        dispatchOrders.find(
          (item) => String(item.id) === String(transit.dispatch_order_id)
        );

      const items = order?.items?.length ? order.items : [transit];

      return items.map((item) => ({
        id: `${transit.id || transit.dispatch_order_id}-${item.id || item.product_id || item.product_barcode_id || 'item'}`,
        dispatchNo: order?.dispatch_no || transit.dispatch_no || transit.dispatch_order_id,
        status: transit.transit_status || order?.dispatch_status || 'intransit',
        source: order?.source || transit.source || '-',
        destination: order?.destination || transit.destination || '-',
        product: getDispatchItemProductName(item) || transit.product_name || '-',
        barcode: getDispatchItemBarcode(item) || transit.bar_code || '-',
        brand: getDispatchItemBrand(item) || transit.brand || '-',
        category: getDispatchItemCategory(item) || transit.category || '-',
        qty: item.qty || item.quantity || transit.qty || transit.quantity,
        unit: getDispatchItemUnit(item) || transit.unit || '-',
        mrp: getSalesMrp(item),
        dprice: getDiscountPrice(item),
        discountPercent: getDiscountPercent(item),
        expDate: item.exp_date || transit.exp_date,
        updatedAt: transit.updated_at || order?.updated_at || order?.created_at,
      }));
    });
  }

  return dispatchOrders
    .filter((order) =>
      ['sent', 'packed', 'label_printed', 'dispatched'].includes(
        String(order.dispatch_status || '').toLowerCase()
      )
    )
    .flatMap((order) =>
      (order.items || []).map((item) => ({
        id: `${order.id}-${item.id || item.product_id || item.product_barcode_id}`,
        dispatchNo: order.dispatch_no || order.id,
        status: 'intransit',
        source: order.source || '-',
        destination: order.destination || '-',
        product: getDispatchItemProductName(item),
        barcode: getDispatchItemBarcode(item),
        brand: getDispatchItemBrand(item),
        category: getDispatchItemCategory(item),
        qty: item.qty || item.quantity,
        unit: getDispatchItemUnit(item),
        mrp: getSalesMrp(item),
        dprice: getDiscountPrice(item),
        discountPercent: getDiscountPercent(item),
        expDate: item.exp_date,
        updatedAt: order.updated_at || order.created_at,
      }))
    );
};

const columns = [
  { key: 'dispatch', label: 'Dispatch', align: 'left', getValue: (row) => row.dispatchNo },
  { key: 'product', label: 'Product', align: 'left', getValue: (row) => row.product },
  { key: 'barcode', label: 'Barcode', align: 'left', getValue: (row) => row.barcode },
  { key: 'brand', label: 'Brand', align: 'left', getValue: (row) => row.brand },
  { key: 'category', label: 'Category', align: 'left', getValue: (row) => row.category },
  {
    key: 'qty',
    label: 'Qty',
    align: 'right',
    type: 'number',
    getValue: (row) => row.qty,
  },
  { key: 'unit', label: 'Unit', align: 'left', getValue: (row) => row.unit },
  {
    key: 'route',
    label: 'Route',
    align: 'left',
    getValue: (row) => `${row.source || ''} ${row.destination || ''}`,
  },
  { key: 'status', label: 'Status', align: 'left', getValue: (row) => row.status },
  {
    key: 'mrp',
    label: 'MRP',
    align: 'right',
    type: 'number',
    getValue: (row) => row.mrp,
  },
  {
    key: 'dprice',
    label: 'D.Price',
    align: 'right',
    type: 'number',
    getValue: (row) => row.dprice,
  },
  {
    key: 'discountPercent',
    label: 'Discount %',
    align: 'right',
    type: 'number',
    getValue: (row) => row.discountPercent,
  },
  {
    key: 'expiry',
    label: 'Expiry',
    align: 'left',
    type: 'date',
    getValue: (row) => row.expDate,
  },
];

const SortHeader = ({ column, sortConfig, onSort }) => (
  <th className={`p-3 ${column.align === 'right' ? 'text-right' : 'text-left'}`}>
    <button
      type="button"
      onClick={() => onSort(column.key)}
      className={`inline-flex w-full items-center gap-1 font-semibold hover:text-blue-700 ${
        column.align === 'right' ? 'justify-end' : 'justify-start'
      }`}
    >
      <span>{column.label}</span>
      <span className="text-xs">
        {sortConfig.key === column.key
          ? sortConfig.direction === 'asc'
            ? '^'
            : 'v'
          : '-'}
      </span>
    </button>
  </th>
);

const TransitProductsTable = ({
  transitProducts = [],
  dispatchOrders = [],
  loading = false,
}) => {
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'dispatch',
    direction: 'asc',
  });

  const rows = useMemo(
    () => getTransitRows(transitProducts, dispatchOrders),
    [transitProducts, dispatchOrders]
  );

  const filteredRows = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return rows;

    return rows.filter((row) =>
      [
        row.dispatchNo,
        row.product,
        row.barcode,
        row.brand,
        row.category,
        row.source,
        row.destination,
        row.status,
        row.mrp,
        row.dprice,
        row.discountPercent,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(value))
    );
  }, [rows, search]);

  const sortedRows = useMemo(
    () => sortRows(filteredRows, sortConfig, columns),
    [filteredRows, sortConfig]
  );

  const handleSort = (key) => {
    setSortConfig((current) => getNextSortConfig(current, key));
  };

  return (
    <section className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-bold">Products In Transit</h2>
          <p className="text-sm text-gray-500">
            Items linked to dispatch orders before they reach destination stock.
          </p>
        </div>

        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search dispatch / product / barcode"
          className="w-full rounded-lg border px-3 py-2 text-sm md:w-80"
        />
      </div>

      {loading ? (
        <p className="py-6 text-center text-gray-500">Loading transit products...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                {columns.map((column) => (
                  <SortHeader
                    key={column.key}
                    column={column}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                ))}
              </tr>
            </thead>

            <tbody>
              {sortedRows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-semibold">{row.dispatchNo}</td>
                  <td className="p-3">{row.product || '-'}</td>
                  <td className="p-3">{row.barcode || '-'}</td>
                  <td className="p-3">{row.brand || '-'}</td>
                  <td className="p-3">{row.category || '-'}</td>
                  <td className="p-3 text-right font-semibold">
                    {formatStockQuantity(row.qty, '-')}
                  </td>
                  <td className="p-3">{row.unit || '-'}</td>
                  <td className="p-3">
                    {row.source || '-'} to {row.destination || '-'}
                  </td>
                  <td className="p-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase ${statusClass(
                        row.status
                      )}`}
                    >
                      {String(row.status || 'intransit').replaceAll('_', ' ')}
                    </span>
                  </td>
                  <td className="p-3 text-right">{formatPrice(row.mrp)}</td>
                  <td className="p-3 text-right font-semibold">{formatPrice(row.dprice)}</td>
                  <td className="p-3 text-right">{formatPercent(row.discountPercent)}</td>
                  <td className="p-3">{formatDispatchDate(row.expDate)}</td>
                </tr>
              ))}

              {sortedRows.length === 0 && (
                <tr>
                  <td colSpan="13" className="p-4 text-center text-gray-500">
                    No products in transit found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default TransitProductsTable;
